import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { GFM_SITES } from "../lib/permitSchemas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Loader2, UserPlus, Pencil, Trash2, ShieldCheck, MapPin } from "lucide-react";

const emptyForm = { email: "", name: "", password: "", role: "site_user", sites: [] };

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user obj or null (create)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () =>
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setUsers([]);
    });

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name || "", password: "", role: u.role, sites: u.sites || [] });
    setDialogOpen(true);
  };

  const toggleSite = (site) =>
    setForm((f) => ({
      ...f,
      sites: f.sites.includes(site) ? f.sites.filter((s) => s !== site) : [...f.sites, site],
    }));

  const save = async () => {
    if (!editing && !form.email) return toast.error("Email is required.");
    if (!editing && form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (editing && form.password && form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (form.role === "site_user" && form.sites.length === 0) return toast.error("Assign at least one site to a site user.");
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, sites: form.sites };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", {
          email: form.email, name: form.name, password: form.password,
          role: form.role, sites: form.sites,
        });
        toast.success("User created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setToDelete(null);
    }
  };

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">GFM - Permit to Work Management System</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">User management</h1>
          <p className="text-sm text-muted-foreground">Create staff accounts and assign the sites they can manage. Admins can see and manage everything.</p>
        </div>
        <Button className="bg-brand hover:bg-brand-dark" onClick={openCreate} data-testid="add-user-btn">
          <UserPlus className="mr-2 h-4 w-4" /> Add user
        </Button>
      </div>

      {users === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-white shadow-sm" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Sites</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <Badge className="bg-brand text-white"><ShieldCheck className="mr-1 h-3 w-3" /> Admin</Badge>
                    ) : (
                      <Badge variant="secondary">Site user</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <span className="text-xs text-muted-foreground">All sites</span>
                    ) : u.sites?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {u.sites.map((s) => (
                          <span key={s} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs">
                            <MapPin className="h-3 w-3 text-brand" /> {s}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-destructive">No sites assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setToDelete(u)} data-testid={`delete-user-${u.email}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update this user's role and assigned sites." : "Create an account and set a temporary password the user can change after signing in."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@company.com" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name" data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Reset password (optional)" : "Temporary password"}</Label>
              <Input type="text" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={editing ? "Leave blank to keep current" : "At least 6 characters"} data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v, sites: v === "admin" ? [] : f.sites }))}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="site_user">Site user (scoped to assigned sites)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "site_user" && (
              <div className="space-y-2">
                <Label>Assigned sites</Label>
                <div className="grid gap-2 rounded-md border border-border p-3">
                  {GFM_SITES.map((s) => (
                    <label key={s} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={form.sites.includes(s)} onCheckedChange={() => toggleSite(s)}
                        data-testid={`site-checkbox-${s}`} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={save} disabled={saving} data-testid="save-user-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.email} will lose access immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-user-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
